<script setup>
import { ref, onMounted, onBeforeUnmount, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { VehicleService } from '@/services/vehicle-service'
import { InspectionService } from '@/services/inspection-service'
import { useVehicleStore, storeReady as vStoreReady } from '@/stores/vehicle-store'
import { useInspectionStore, storeReady as iStoreReady } from '@/stores/inspection-store'
import { mdiCarInfo } from '@mdi/js'
import { Notify } from '@/modules/util/notify'
import { Settings } from '@/settings'
import { HttpCache } from '@/modules/cache/http'
import CustomerField from '@/components/autoform/fields/CustomerField.vue'

const vehicleService = new VehicleService
const inspectionService = new InspectionService
const route = useRoute()
const router = useRouter()
const vehicleStore = useVehicleStore()
const inspectionStore = useInspectionStore()
const { INSPECTION_TYPES } = Settings

const cached = Settings.getProgress() || {}
const cache = new HttpCache

const vehicle = ref(null)
const vendorId = ref(null)
const labels = ref({})
const inspections = ref([])
const selected = ref(null)
const showContinue = ref(false)
const saveInProgress = ref(false)
const showAll = ref(false)
const loading = ref(true)

const inspectionTypes = INSPECTION_TYPES.reduce((acc, type) => {
	acc[type.id] = type
	return acc
}, {})

const inspectionTypeFilter = (type, vehicle) => {
	if (!type.data?.filters) {
		return true
	}

	vehicle.logisticslocation_id = Number(localStorage.getItem('LOCATION_ID')) 
		|| undefined 

	let include = true

	for (let [key, val] of Object.entries(type.data.filters)) {
		if (vehicle[key] == undefined) {
			if (key.startsWith('motorvehicle_')) {
				key = key.substring(13)
			} else if (key.startsWith('motorvehicle')) {
				key = key.substring(12)
			}

			if (vehicle[key] == undefined) {
				continue
			}
		}

		if (key == 'classification_id' && showAll.value) {
			continue
		}

		let cmp = true

		if (val.value) {
			if (val.not != undefined && val.not) {
				cmp = false
			} 

			val = val.value
		}

		if (!Array.isArray(val)) {
			val = [val]
		}

		include = include && !!val.find(v => v == vehicle[key]) == cmp
	}

	return include
}

const displayInfo = ref([
	{ label: 'Make / Model / Variant', value: ' ' },
	{ label: 'Colour', value: ' ' },
	{ label: 'Gearbox', value: ' ' },
	{ label: 'VRM', value: ' ' },
	{ label: 'Grade', value: ' ' },
])

const displayLinks = ref(Object.entries(inspectionTypes)
	.sort((a, b) => Number(a[1].order) < Number(b[1].order) ? -1 : 1)
	.map(type => ({ 
		to: 'inspection', 
		label: type[1].title, 
		name: type[1].name,
		params: { typeId: type[0] }, 
		loading: false,
	}))
)

const getDisplayInfo = (vehicle) => {	
	return vehicle.type_id != 2 
		? [{
			label: 'Make / Model / Variant',
			value: `${vehicle.manufacturer} ${vehicle.model} ${vehicle.variant}`,
		}, {
			label: 'Colour',
			value: vehicle.colour,
		}, {
			label: 'Gearbox',
			value: vehicle.gearboxtype,
		}, {
			label: 'VRM',
			value: vehicle.registration,
		}, {
			label: vehicle.namagrade ? 'NAMA Grade' : vehicle.exteriorgrade ? 'Exterior Grade' : 'No Grade',
			value: vehicle.namagrade || vehicle.exteriorgrade || '',
		}, {
			label: labels.value.capclean || 'CAP Clean',
			value: vehicle.capclean,
		}, {
			label: labels.value.capaverage || 'CAP Average',
			value: vehicle.capaverage,
		}, {
			label: labels.value.capbelow || 'CAP Below',
			value: vehicle.capbelow,
		}] 
		: [{
			label: 'Make / Model / Variant',
			value: `${vehicle.manufacturer} ${vehicle.model} ${vehicle.variant}`,
		}, {
			label: 'Details',
			value: vehicle.details,
		}, {
			label: 'Contract No.',
			value: vehicle.contractnumber,
		}, {
			label: 'Loose Items',
			value: vehicle.looseitems,
		}, {
			label: 'Loose Item Ref #',
			value: vehicle.looseitemref,
		}]
}

const getDisplayLinks = (vehicle) => {
	return Object.entries(inspectionTypes)
		.sort((a, b) => Number(a[1].order) < Number(b[1].order) ? -1 : 1)
		.filter((type) => inspectionTypeFilter(type[1], vehicle))
		.map(type => ({ 
			to: 'inspection', 
			label: type[1].title, 
			name: type[1].name,
			params: { typeId: type[0] }, 
			loading: false,
			resume: String(vehicle.id) === cached.vehicleId && type[0] === cached.inspectionId
		}))
}

const getVehicle = async () => {
	await vStoreReady

	const promise = vehicleService.get(route.params.id).then(res => {
		if (!(res.body?.status && res.body?.vehicle)) {
			throw new Error(JSON.stringify(res.body))
		}

		labels.value = res.body.labels
		vehicle.value = res.body.vehicle

		vehicleStore.setCurrentVehicle(res.body.vehicle)	
	}).catch(e => {
		if (e === 'UserAbort')
			return

		Notify.warning('Could not refresh vehicle details')
		console.warn('getVehicle:', e.message || e)
	}).finally(() => {
		loading.value = false
	})
    
	let existing = (vehicleStore.allVehicles || []).find(vehicle => vehicle.id == route.params.id)
	if (existing)
		vehicle.value = existing

	if (!vehicle.value) {
		await promise
		return
	}
    
	vehicleStore.setCurrentVehicle(vehicle.value)
}

const listInspections = async () => {
	await iStoreReady

	inspectionService.list(route.params.id).then(res => {
		if (!(res.body?.status && res.body?.previousinspections)) {
			throw new Error(JSON.stringify(res.body))
		}
		
		inspections.value = res.body.previousinspections.map(inspection => {
			inspection.motorvehicleinspection_data = JSON.parse(inspection.motorvehicleinspection_data)
			inspection.display_datetime = inspection.motorvehicleinspection_datetime.split(' ').map((v, i) => {
				if (i) return v
				return v.split('-').reverse().join('/')
			}).join(' ')
			return inspection
		})

		inspectionStore.setInspections(inspections.value)
	}).catch(e => {
		console.warn('listInspections:', e.message || e)
		Notify.warning('Could not refresh inspection history')
	})

	inspections.value = (inspectionStore.allInspections || []).filter(inspection => inspection.motorvehicle_id == route.params.id)
	inspectionStore.setInspections(inspections.value)
}

const selectInspectionLink = async (link) => {
	const cached = await cache.find(req => {
		if (req.value.path !== '/saveinspection') 
			return false
        
		const body = JSON.parse(req.value.request.body)

		return body.inspectiontype_id == link.params.typeId
			&& body.motorvehicle_id == vehicle.value.id
	})

	if (cached.length) {
		saveInProgress.value = true
		return
	}

	selected.value = null
	let previous

	for (const i of inspections.value) {
		if (i.motorvehicleinspectiontype_id == link.params.typeId) {
			previous = i
			break
		}
	}

	if (previous) {
		selected.value = previous
		showContinue.value = true
		return
	}

	link.loading = true 
	router.push({ name: link.to, params: link.params })
}

watch([showAll, labels], () => {
	displayLinks.value = getDisplayLinks(vehicle.value)
})

watch(vehicle, () => {
	displayLinks.value = getDisplayLinks(vehicle.value)
	displayInfo.value = getDisplayInfo(vehicle.value)
	vendorId.value = vehicle.value.vendor_id
})

onMounted(async () => {
	await Promise.all([
		getVehicle(),
		listInspections(),
	])
})

onBeforeUnmount(() => {
	vehicleService.cancel().get(route.params.id)
})
</script>

<template>
<v-container>
	<v-card
		title="Vehicle Details"
		:prepend-icon="mdiCarInfo"
		max-width="500"
		class="mx-auto"
		:loading="loading"
		hover
	>
		<v-list lines="two">
			<CustomerField
				:field="{ label: 'Vendor', listitem: true }"
				:model-value="vendorId"
			/>
			<template
				v-for="info in displayInfo"
				:key="info.label"
			>
				<v-list-item>
					<v-list-item-subtitle>{{ info.label }}</v-list-item-subtitle>
					<v-list-item-title>{{ info.value || '' }}</v-list-item-title>
				</v-list-item>
				<v-divider />
			</template>
		</v-list>
            
		<v-container v-if="vehicle">
			<v-row>
				<v-col class="py-0">
					<v-switch 
						v-model="showAll"
						label="Show all inspections"
						hide-details="auto"
						color="primary"
					/>
				</v-col>
			</v-row>
			<template
				v-for="link in displayLinks"
				:key="link.label"
			>
				<v-row>
					<v-col class="d-flex">
						<v-btn
							:loading="link.loading"
							color="primary"
							variant="tonal"
							class="flex-grow-1"
							@click="selectInspectionLink(link)"
						>
							{{ link.label || inspectionTypes[link.params.typeId].title }}
						</v-btn>
						<v-btn
							v-if="link.resume"
							variant="tonal"
							color="success"
							class="ms-2"
							@click="router.push({ name: link.to, params: link.params })"
						>
							Resume
						</v-btn>
					</v-col>
				</v-row>
			</template>
		</v-container>

		<v-expansion-panels>
			<v-expansion-panel
				title="Inspection History"
				class="pb-2"
			>
				<v-expansion-panel-text>
					<v-list lines="two">
						<template
							v-for="inspection in inspections"
							:key="inspection.motorvehicleinspection_id"
						>
							<v-list-item
								@click="router.push({ 
									name: 'inspection', 
									params: { 
										typeId: inspection.motorvehicleinspectiontype_id,
										inspectionId: inspection.motorvehicleinspection_id 
									} 
								})"
							>
								<v-list-item-subtitle>
									{{ inspectionTypes[inspection.motorvehicleinspectiontype_id]?.title }}
								</v-list-item-subtitle>
								<v-list-item-title>
									{{ inspection.display_datetime }}
								</v-list-item-title>
							</v-list-item>
						</template>
					</v-list>
				</v-expansion-panel-text>
			</v-expansion-panel>
		</v-expansion-panels>
	</v-card>
</v-container>

<v-dialog v-model="showContinue">
	<v-card
		:title="inspectionTypes[selected.motorvehicleinspectiontype_id].title"
		class="mx-auto"
	>
		<v-card-text>Previous inspection found</v-card-text>
		<v-card-actions>
			<v-btn
				color="success"
				@click="router.push({ 
					name: 'inspection', 
					params: { 
						typeId: selected.motorvehicleinspectiontype_id,
						inspectionId: selected.motorvehicleinspection_id 
					} 
				})"
			>
				Continue
			</v-btn>
			<v-btn
				color="error"
				@click="router.push({ 
					name: 'inspection', 
					params: { typeId: selected.motorvehicleinspectiontype_id } 
				})"
			>
				Start New
			</v-btn>
			<v-btn
				color="light"
				@click="showContinue = false"
			>
				Back
			</v-btn>
		</v-card-actions>
	</v-card>
</v-dialog>

<v-dialog v-model="saveInProgress">
	<v-card
		title="Save In Progress"
		class="mx-auto"
	>
		<v-card-text>
			This inspection is currently uploading, please wait until it has completed before continuing.
		</v-card-text>
		<v-card-actions>
			<v-btn
				color="error"
				@click="saveInProgress = false"
			>
				Cancel
			</v-btn>
		</v-card-actions>
	</v-card>
</v-dialog>
</template>

<style lang="scss" scoped>

</style>